import { ReactNode, SVGProps } from 'react'
import { AxisTickProps } from '@nivo/axes'
import { BarCustomLayerProps, BarDatum } from '@nivo/bar'
import dynamic from 'next/dynamic'
import { ChartContainer, Skeleton, Stack, StackProps } from '~/design_system'
import { SadStates } from '~/components/SadStates'
import { SaferMessageDescriptor, useMsg } from '~/common/localizationUtils'
import { useChartFormat, useFormattedTitle } from './format'
import { CustomLinkChartProps, GenericChartProps, ReportKeyChartProps, Datum } from './ChartTypes'

// nivo elements have to be dynamically imported because d3 went ESM only and nextjs doesn't like it
const ResponsiveBar = dynamic(() => import('@nivo/bar').then(module => module.ResponsiveBar), { ssr: false })

const MAX_VERTICAL_LABEL_LENGTH = 18
const MAX_HORIZONTAL_LABEL_LENGTH = 8

const getBarLabelPosition = (layout: string, width: number, height: number) =>
  layout === 'horizontal'
    ? ({ x: width + 8, y: height / 2, textAnchor: 'start' } as const)
    : ({ x: width / 2, y: -10, textAnchor: 'middle' } as const)

const Bars =
  (layout: string) =>
  ({ bars }: BarCustomLayerProps<BarDatum>) => {
    return bars.map(({ x, y, width, height, color, data }, idx) => {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <g transform={`translate(${x}, ${y})`} key={idx}>
          <rect width={width} height={height} rx="0" ry="0" fill={color} strokeWidth="0" stroke={color} />
          {/* getBarLabelPosition returns textAnchor as `string`; cast to SVG props (see customTick). */}
          <text
            {...getBarLabelPosition(layout, width, height)}
            dominantBaseline="central"
            style={{ fontFamily: 'sans-serif', fontSize: '14px', pointerEvents: 'none' }}>
            {data.formattedValue}
          </text>
        </g>
      )
    })
  }

const getChartProps = (layout: string) => {
  return layout === 'horizontal'
    ? {
        colors: '#26b999',
        enableGridX: true,
        enableGridY: false,
        gridXValues: [0],
        margin: { bottom: 24, left: 160, right: 64, top: 24 }
      }
    : {
        colors: '#3077cc',
        enableGridX: false,
        enableGridY: true,
        gridYValues: [0],
        margin: { bottom: 48, left: 12, right: 12, top: 24 },
        xAxisLabelOffset: 40,
        yAxisLabelOffset: -60
      }
}

const getDefaultSubtitle = (count = 0, msg: (id: ReactNode | SaferMessageDescriptor, values?: any) => string) => {
  return count >= 10 ? msg('common.ui.top_results') : msg('common.ui.results')
}

type BaseBarChartProps = GenericChartProps

interface CustomLinkBarChartProps extends BaseBarChartProps, CustomLinkChartProps {}

interface ReportKeyBarChartProps extends BaseBarChartProps, ReportKeyChartProps {}

export type BarChartProps = CustomLinkBarChartProps | ReportKeyBarChartProps

/**
 * Generates an array of logarithmic scale values for chart axis ticks
 * @param maxValue The maximum value in the dataset
 * @param steps The number of steps/ticks to generate
 * @returns An array of logarithmic scale values
 */
export const logScale = (maxValue: number, steps: number): number[] => {
  if (maxValue === 0) return [0]
  const isNegative = maxValue < 0
  const result: number[] = [0]
  const base = Math.abs(maxValue) ** (1 / steps)

  for (let i = 1; i <= steps; i++) {
    let stepCalc = Math.round(base ** i)
    if (isNegative) stepCalc *= -1
    result.push(stepCalc)
  }
  return [...new Set(result)]
}

export const BarChart = ({
  data,
  chartConfig,
  reportKey,
  customReportUrl,
  isLoading = true,
  isFetching = true,
  isError = false,
  isEmpty = false
}: BarChartProps) => {
  const msg = useMsg()
  const {
    format = 'number',
    xAxisLabel,
    yAxisLabel,
    title = `${xAxisLabel} By ${yAxisLabel}`,
    layout = 'horizontal',
    subTitle,
    xAxisFormat = (label: string) => label,
    yAxisFormat = (label: string) => label,
    scaleType = 'linear'
  } = chartConfig

  const props = getChartProps(layout)
  const chartFormat = useChartFormat('number')
  // Nivo must be hijacking the data prop because the .reverse method is not working
  //   any longer but by cloning the data prop, it works again
  const clonedData = JSON.parse(JSON.stringify(data || []))

  // Custom scale values for the logarithmic scale chart types
  let tickValues: number[] | number = 0
  if (scaleType === 'symlog' || scaleType === 'log') {
    const dataVals = clonedData
      .map((d: Datum) => Math.round(Number(d.value)))
      .sort((a: number, b: number): number => b - a)
    const maxVal = dataVals[0]
    tickValues = logScale(maxVal, 3)
  }

  return (
    <ChartContainer
      title={useFormattedTitle(title, format)}
      subTitle={subTitle || (layout === 'horizontal' ? getDefaultSubtitle(data?.length, msg) : '')}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      isEmpty={isEmpty}
      reportKey={reportKey}
      customReportUrl={customReportUrl}>
      <SadStates
        states={[
          {
            when: isLoading,
            render: () => (layout === 'horizontal' ? <HorizontalBarChartSkeleton /> : <VerticalBarChartSkeleton />)
          }
        ]}>
        {data && (
          <ResponsiveBar
            data={layout === 'horizontal' ? clonedData?.reverse() : clonedData}
            layout={layout}
            {...props}
            valueFormat={chartFormat}
            layers={['grid', 'axes', Bars(layout), 'markers', 'legends', 'annotations']}
            padding={0.4}
            axisBottom={
              scaleType === 'symlog' || scaleType === 'log'
                ? { tickSize: 4, tickRotation: 0, format: xAxisFormat, tickValues }
                : {
                    tickSize: 0,
                    tickValues,
                    format: xAxisFormat,
                    renderTick: props => customTick(props, 'horizontal')
                  }
            }
            axisLeft={
              layout === 'horizontal'
                ? {
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: 0,
                    format: yAxisFormat,
                    renderTick: props => customTick(props, 'vertical')
                  }
                : null
            }
            valueScale={{ type: scaleType ?? 'linear' }}
          />
        )}
      </SadStates>
    </ChartContainer>
  )
}

function customTick(
  { opacity, textAnchor, textBaseline, textX, textY, value, x, y, format }: AxisTickProps<any>,
  orientation: string
) {
  const formattedValue = format ? format(value) : value
  const maxLabelLength = orientation === 'vertical' ? MAX_VERTICAL_LABEL_LENGTH : MAX_HORIZONTAL_LABEL_LENGTH
  const truncatedValue =
    formattedValue.length > maxLabelLength ? `${formattedValue.substr(0, maxLabelLength)}…` : formattedValue
  return (
    <g transform={`translate(${x},${y})`} style={{ opacity }}>
      {/* nivo types textBaseline/textAnchor as plain `string`; React 19's SVG types want the
          precise enum unions. The values nivo emits are valid SVG keywords, so we assert that. */}
      <text
        fontSize="14px"
        dominantBaseline={textBaseline as SVGProps<SVGTextElement>['dominantBaseline']}
        textAnchor={textAnchor as SVGProps<SVGTextElement>['textAnchor']}
        transform={`translate(${textX},${textY})`}>
        <title>{formattedValue}</title>
        {truncatedValue}
      </text>
    </g>
  )
}

const HorizontalBarChartSkeleton = (props: StackProps) => {
  const widths = [96, 64, 48, 40, 32, 24, 18, 6]
  return (
    <Stack p={8} spacing={3} width="full" {...props}>
      {widths.map((width, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Skeleton h="25px" width={`${width}%`} key={idx} />
      ))}
    </Stack>
  )
}

const VerticalBarChartSkeleton = (props: StackProps) => {
  const hideOnMobile = { display: { base: 'none', sm: 'block' } }

  return (
    <Stack
      p={8}
      spacing={5}
      direction="row"
      height="full"
      width={{ base: '80%', sm: '70%' }}
      mx="auto"
      alignItems="flex-end"
      {...props}>
      <Skeleton w="35px" height="18%" />
      <Skeleton w="35px" height="24%" {...hideOnMobile} />
      <Skeleton w="35px" height="32%" />
      <Skeleton w="35px" height="40%" {...hideOnMobile} />
      <Skeleton w="35px" height="48%" {...hideOnMobile} />
      <Skeleton w="35px" height="64%" />
      <Skeleton w="35px" height="96%" />
    </Stack>
  )
}
